/* import __COLOCATED_TEMPLATE__ from './advanced-settings-websync.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type { TrackedArray } from 'tracked-built-ins';
import type IntlService from 'embercom/services/intl';
import type { DuplicateSource } from 'embercom/components/content-service/ai-content-library/sources/add-source-modal';
import type RouterService from '@ember/routing/router-service';
import type ContentImportSource from 'embercom/models/content-service/content-import-source';
import { EntityType } from 'embercom/models/data/entity-types';

export type addModalUpdateParams = {
  additionalUrls: string;
  removeCssSelectors: string;
  clickCssSelector: string;
  waitForSelector: string;
  useSitemap: boolean;
  relativeDateMonths?: number;
  dateSelector?: string;
  includePagesWithNoDate?: boolean;
  useDateCrawler?: boolean;
};

export interface Args {
  form: any;
  sourceUrl?: string;
  updateExcludedUrl: (index: number, event: InputEvent) => void;
  deleteExcludedUrl: (index: number) => void;
  addExcludedUrl: () => void;
  toggleSitemap: () => void;
  updateValues: (params: addModalUpdateParams) => void;
  checkAdditionalUrlsAreNotAlreadySynced: () => void;
  additionalUrlsDuplicateSource: DuplicateSource | null;
  isRemoveCssSelectorInvalid: boolean;
  isClickCssSelectorInvalid: boolean;
  isWaitForSelectorInvalid: boolean;
  isDateSelectorInvalid: boolean;
  isRelativeDateInvalid: boolean;
  isNewSource: boolean;
  isAddingNewSource: boolean;
  isFormInvalid?: boolean;
  isAdditionalUrlsInvalid?: boolean;
  additionalUrls: string;
  excludedUrls: TrackedArray<string>;
  removeCssSelectors: string;
  clickCssSelector: string;
  useSitemap: boolean;
  waitForSelector: string;
  relativeDateMonths?: number;
  dateSelector?: string;
  includePagesWithNoDate?: boolean;
  useDateCrawler?: boolean;
  contentImportSource?: ContentImportSource | undefined | null;
}

export default class AdvancedSettingsWebsync extends Component<Args> {
  @service declare intercomEventService: any;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare router: RouterService;

  @tracked additionalUrls = this.args.additionalUrls;
  @tracked useSitemap = this.args.useSitemap;
  @tracked includePagesWithNoDate = this.args.includePagesWithNoDate || false;
  @tracked useDateCrawler = this.args.useDateCrawler ?? false;
  @tracked relativeDateMonths = this.args.relativeDateMonths;
  @tracked dateSelector = this.args.dateSelector;

  sourceUrl = this.args.sourceUrl || '';
  removeCssSelectors = this.args.removeCssSelectors;
  clickCssSelector = this.args.clickCssSelector;
  waitForSelector = this.args.waitForSelector;

  @action
  deleteExcludedUrl(index: number) {
    this.args.deleteExcludedUrl(index);
  }

  @action
  addExcludedUrl() {
    this.args.addExcludedUrl();
  }

  @action
  toggleSitemap() {
    this.useSitemap = !this.useSitemap;
    this.trackAnalyticsEvent('clicked', 'use_sitemap_switch');
    this.onFieldUpdate();
  }

  @action
  toggleIncludePagesWithNoDate() {
    this.includePagesWithNoDate = !this.includePagesWithNoDate;
    this.onFieldUpdate();
    this.trackAnalyticsEvent('clicked', 'include_pages_with_no_date_switch');
  }

  @action
  toggleDateCrawler() {
    this.useDateCrawler = !this.useDateCrawler;

    if (this.useDateCrawler) {
      this.relativeDateMonths ??= 6;
    } else if (this.args.isNewSource) {
      this.relativeDateMonths = undefined;
      this.dateSelector = undefined;
      this.includePagesWithNoDate = false;
    }

    this.trackAnalyticsEvent('clicked', 'date_options_switch');
    this.onFieldUpdate();
  }

  @action
  onFieldUpdate() {
    let params = {
      additionalUrls: this.additionalUrls,
      removeCssSelectors: this.removeCssSelectors,
      clickCssSelector: this.clickCssSelector,
      waitForSelector: this.waitForSelector,
      useSitemap: this.useSitemap,
      relativeDateMonths: this.relativeDateMonths,
      dateSelector: this.dateSelector,
      includePagesWithNoDate: this.includePagesWithNoDate,
      useDateCrawler: this.useDateCrawler,
    } as addModalUpdateParams;
    this.args.updateValues(params);
  }

  @action checkAdditionalUrlsAreNotAlreadySynced() {
    this.args.checkAdditionalUrlsAreNotAlreadySynced();
  }

  @action trackRemoveCssSelectorAnalytics() {
    this.trackAnalyticsEvent('inputed', 'remove_css_selector_input');
  }

  @action trackClickCssSelectorAnalytics() {
    this.trackAnalyticsEvent('inputed', 'click_css_selector_input');
  }

  @action trackWaitForSelectorAnalytics() {
    this.trackAnalyticsEvent('inputed', 'wait_for_selector_input');
  }

  @action updateExcludedUrl(index: number, event: InputEvent) {
    this.args.updateExcludedUrl(index, event);
  }

  get contentImportSourceEntityType(): EntityType {
    return EntityType.ContentImportSource;
  }

  get contentImportSourceId(): number {
    return Number(this.args.contentImportSource?.id);
  }

  get contentImportSourceContentSegmentIds(): number[] {
    return this.args.contentImportSource?.contentSegmentIds ?? [];
  }

  get canApplyAudienceSegmentsToWebSyncs(): boolean {
    return this.appService.app.canApplyAudienceSegmentsToWebSyncs;
  }

  get isRemoveCssSelectorInvalid(): boolean {
    return this.args.isRemoveCssSelectorInvalid;
  }

  get isClickCssSelectorInvalid(): boolean {
    return this.args.isClickCssSelectorInvalid;
  }

  get isWaitForSelectorInvalid(): boolean {
    return this.args.isWaitForSelectorInvalid;
  }

  get isRelativeDateRequired(): boolean {
    return this.useDateCrawler && this.relativeDateMonths === undefined;
  }

  get additionalUrlsErrorMessage(): string {
    if (this.args.additionalUrlsDuplicateSource?.folderId) {
      let otherSourceRedirect = this.router.urlFor(
        'apps.app.knowledge-hub.folder',
        this.args.additionalUrlsDuplicateSource.folderId,
      );
      return this.intl.t(
        'ai-content-library.sources.add-source-modal.additional-urls-new.duplicate-url',
        {
          offendingUrl: this.args.additionalUrlsDuplicateSource.offendingUrl,
          otherSourceRedirect,
          htmlSafe: true,
        },
      );
    }
    if (this.additionalUrls.length >= 65000) {
      return this.intl.t(
        'ai-content-library.sources.add-source-modal.additional-urls.url-too-long',
      );
    }
    if (this.args.isNewSource) {
      return this.intl.t(
        'ai-content-library.sources.add-source-modal.additional-urls-new.validation-error',
      );
    }
    return this.intl.t(
      'ai-content-library.sources.add-source-modal.additional-urls-new.validation-error-edit',
      { url: this.sourceUrl },
    );
  }

  private trackAnalyticsEvent(action: string, object: string): void {
    this.intercomEventService.trackAnalyticsEvent({
      action,
      object,
      context: 'side_drawer',
      place: 'external_content',
      section: 'operator',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'ContentService::AiContentLibrary::Sources::AdvancedSettingsWebsync': typeof AdvancedSettingsWebsync;
  }
}
